/**
 * Информационные экраны Mini App: FAQ (ШАГ 20), Коммьюнити (ШАГ 19),
 * «Как это работает» (ШАГ 22) и выбор языка (ШАГ 17).
 *
 * Открываются шторкой снизу — приём из design-spec §5.5.
 * Тексты приходят с сервера (`/api/texts`), поэтому словарь один на бота и приложение.
 */
import { SheetPortal } from '../components/SheetPortal.js';
import { api } from '../lib/api.js';
import type { AppConfig } from '../lib/api.js';
import { haptic, openLink } from '../lib/telegram.js';
import { useApp } from '../state/app.js';

export type SheetId = 'faq' | 'community' | 'how' | 'language' | null;

/** Языки экрана ШАГ 17 — подписи на своём языке. */
const LANGUAGES: Array<{ code: string; label: string }> = [
  { code: 'ar', label: 'العربية' },
  { code: 'de', label: 'Deutsch' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'id', label: 'Bahasa Indonesia' },
  { code: 'it', label: 'Italiano' },
  { code: 'fil', label: 'Filipino' },
  { code: 'pt', label: 'Português' },
  { code: 'ru', label: 'Русский' },
];

/** Пункты FAQ: те же ключи, что в боте. */
const FAQ_ITEMS = [1, 2, 3, 4, 5];

/**
 * Тексты словаря общие с ботом и содержат эмодзи, а кнопки/шторки прилы
 * с Cosmic-прохода живут без них — чистим на клиенте, словарь не трогаем.
 */
const stripEmoji = (text: string): string =>
  text
    .replace(/[\p{Extended_Pictographic}️‍]/gu, '')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/[ \t]+([.,!?:;\n])/g, '$1')
    .split('\n')
    .map((line) => line.trim())
    .join('\n')
    .trim();

/** Тело FAQ в боте начинается с повтора вопроса («1. …») — в шторке он лишний. */
const faqBody = (body: string): string => {
  const lines = body.split('\n');
  return stripEmoji((/^\d+\.\s/.test(lines[0] ?? '') ? lines.slice(1) : lines).join('\n'));
};

/** Фолбэк на время загрузки конфига — те же пороги, что в core/config/funnel.ts. */
const DEFAULT_TIERS = [
  { minDeposit: 20, maxDeposit: 49.99, tokens: 50 },
  { minDeposit: 50, maxDeposit: 99.99, tokens: 150 },
  { minDeposit: 100, maxDeposit: 199.99, tokens: 350 },
  { minDeposit: 200, maxDeposit: null, tokens: 500 },
];

const tierRange = (tier: { minDeposit: number; maxDeposit: number | null }): string =>
  tier.maxDeposit === null
    ? `$${tier.minDeposit}+`
    : `$${tier.minDeposit}–${Math.floor(tier.maxDeposit)}`;

const contactUrl = (contact: string): string => `https://t.me/${contact.replace(/^@/, '')}`;

/**
 * Контакт ещё не заполнен (пустой или заглушка) — кнопку показывать нельзя:
 * она увела бы человека на `t.me/TODO`, то есть в никуда.
 */
const hasContact = (contact: string | undefined): contact is string =>
  typeof contact === 'string' && contact.length > 1 && !contact.toUpperCase().includes('TODO');

export function InfoSheet({
  id,
  config,
  onClose,
}: {
  id: SheetId;
  config: AppConfig | null;
  onClose: () => void;
}) {
  const { me, t, refresh, showToast, reloadTexts } = useApp();

  if (id === null) return null;

  async function switchLanguage(code: string): Promise<void> {
    haptic();
    try {
      await api.updateSettings({ language: code });
      await Promise.all([reloadTexts(code), refresh()]);
      showToast(t('toast.language'));
      onClose();
    } catch {
      showToast(t('common.error'));
    }
  }

  return (
    <SheetPortal>
    <div
      className="sheet-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="sheet">
        <div className="sheet-handle" />

        {id === 'faq' && (
          <>
            <span className="kicker">{config?.brand.name ?? ''}</span>
            <h2 className="sheet-title-grad">{t('app.more.faq')}</h2>
            <p className="faq-intro">{t('app.faq.intro')}</p>
            {/* пункты раскрываются по одному, как в боте: простыню из пяти вопросов не читают */}
            {FAQ_ITEMS.map((index) => (
              <details key={index} className="faq-item">
                <summary className="faq-question">{stripEmoji(t(`faq.${index}.title`))}</summary>
                <p className="faq-answer">{faqBody(t(`faq.${index}.body`))}</p>
              </details>
            ))}
          </>
        )}

        {id === 'how' && (
          <>
            <span className="kicker">{config?.brand.name ?? ''}</span>
            <h2 className="sheet-title-grad">{t('app.more.how')}</h2>

            <div className="how-steps">
              {[1, 2, 3].map((step) => (
                <div key={step} className="how-step">
                  <span className="how-step-num">{step}</span>
                  <span className="how-step-text">{t(`app.how.step${step}`)}</span>
                </div>
              ))}
            </div>

            <span className="risk-label">{t('app.how.tiers_title')}</span>
            <div className="tier-list">
              {(config?.referralTiers ?? DEFAULT_TIERS).map((tier) => (
                <div key={tier.minDeposit} className="tier-row">
                  <span className="tier-range">
                    {t('app.how.deposit')} {tierRange(tier)}
                  </span>
                  <span className="tier-tokens">+{tier.tokens}</span>
                </div>
              ))}
            </div>

            <p className="how-footer">{t('app.how.footer')}</p>
          </>
        )}

        {id === 'community' && (
          <>
            <span className="kicker">{config?.brand.name ?? ''}</span>
            <h2 className="sheet-title-grad">{t('app.more.community')}</h2>
            <p className="sheet-text">{stripEmoji(t('screen.community.body'))}</p>
            <div className="stack" style={{ marginTop: 14 }}>
              {hasContact(config?.brand.communityChannel) && (
                <button
                  type="button"
                  className="cta cta-block"
                  onClick={() => openLink(contactUrl(config.brand.communityChannel))}
                >
                  {t('app.community.main')}
                </button>
              )}
              {hasContact(config?.brand.supportContact) && (
                <button
                  type="button"
                  className="cta cta-secondary cta-block"
                  onClick={() => openLink(contactUrl(config.brand.supportContact))}
                >
                  {t('app.community.support')}
                </button>
              )}
            </div>
          </>
        )}

        {id === 'language' && (
          <>
            <span className="kicker">{config?.brand.name ?? ''}</span>
            <h2 className="sheet-title-grad">{t('app.more.language')}</h2>
            <div className="lang-grid">
              {LANGUAGES.map((language) => (
                <button
                  key={language.code}
                  type="button"
                  className={`chip ${me?.user.language === language.code ? 'is-active' : ''}`}
                  onClick={() => void switchLanguage(language.code)}
                >
                  {language.label}
                </button>
              ))}
            </div>
          </>
        )}

        <button type="button" className="cta cta-secondary cta-block" style={{ marginTop: 16 }} onClick={onClose}>
          {t('app.common.back')}
        </button>
      </div>
    </div>
    </SheetPortal>
  );
}

/** Список «Ещё» на экране аккаунта: точки входа в информационные экраны. */
export function InfoLinks({ onOpen }: { onOpen: (id: SheetId) => void }) {
  const { t } = useApp();

  const rows: Array<{ id: Exclude<SheetId, null>; labelKey: string }> = [
    { id: 'how', labelKey: 'app.more.how' },
    { id: 'faq', labelKey: 'app.more.faq' },
    { id: 'community', labelKey: 'app.more.community' },
    { id: 'language', labelKey: 'app.more.language' },
  ];

  return (
    <section className="card">
      <span className="kicker">{t('screen.menu.title')}</span>
      <div className="info-list">
        {rows.map((row) => (
          <button
            key={row.id}
            type="button"
            className="info-row"
            onClick={() => {
              haptic();
              onOpen(row.id);
            }}
          >
            {/* аврора-маркер вместо эмодзи (Cosmic-проход) */}
            <span className="info-dot" />
            <span className="grow">{t(row.labelKey)}</span>
            <span className="info-chevron">›</span>
          </button>
        ))}
      </div>
    </section>
  );
}
